/** This is a panel that appears on the Study overview, Activity, and Request workflow pages */
// NOTE: edit comment modal is on this component; delete modal is in TimelineFeedItem
import React from "react"
import dayjs from "dayjs"
import { PlusOutlined } from "@ant-design/icons"
import PropTypes from "prop-types"
import { Panel } from "components-design-system"
import Modal from "components/Modal/Modal"
import PrimeField from "components/PrimeField/PrimeField"
import { Dropdown, Divider, Space, theme } from "antd"
import PrimeButton from "components-design-system/PrimeButton/PrimeButton"
import TimelineFeedItem from "components/TimelineFeed/TimelineFeedItem/TimelineFeedItem"
import FeedCommentEditForm from "components/TimelineFeed/FeedCommentEditForm/FeedCommentEditForm"
import "./TimelineFeed.scss"

// assets
import emptyFeedLogo from "assets/images/empty-feed.png"

// apis
import { useUserPermissions } from "api/hooks"

const { useToken } = theme

const TimelineFeed = ({
  type,
  timeline,
  modalMsg,
  refetchTimeline,
  timelineFeedParams,
  setTimelineFeedParams,
  panelClassName,
  style = {},
}) => {
  const [selectedLabels, setSelectedLabels] = React.useState({})
  const [selectedComment, setSelectedComment] = React.useState(null)
  const [openEditCommentModal, setOpenEditCommentModal] = React.useState(null)
  const [showAllComments, setShowAllComments] = React.useState(false)
  const [activeKeys, setActiveKeys] = React.useState(["all"])
  const { token } = useToken()
  const { data: permissions } = useUserPermissions()
  const canUserCreate = permissions?.["project.comment.create"] === true || permissions?.["study.comment.create"]

  // getting tags on data for activity versus all tags
  const tags = React.useMemo(() => {
    if (Array.isArray(timeline)) {
      let timelineTags = []
      timeline.forEach((item) => {
        if (item.tags) {
          timelineTags = [...timelineTags, ...item.tags]
        }
      })
      return [...new Set(timelineTags)]
    }
  }, [timeline])

  React.useEffect(() => {
    getTimelineItems(false)
  }, [showAllComments, selectedLabels])

  const getTimelineItems = async (getMore) => {
    const feedParams = {
      labels: selectedLabels,
      [type === "study" ? "showActivity" : "showStudy"]: showAllComments,
    }
    if (setTimelineFeedParams) setTimelineFeedParams(feedParams)
  }

  const onEditComment = (comment = null) => {
    setSelectedComment(comment)
    setOpenEditCommentModal(true)
  }

  const onCloseEditComment = () => {
    setSelectedComment(null)
    setOpenEditCommentModal(false)
  }

  const onChangeLabels = (val) => {
    let selectedLabels = {}
    for (let i = 0; i < val.length; i++) {
      let currItem = val[i]
      selectedLabels[currItem] = currItem
    }
    setSelectedLabels(selectedLabels)
  }

  const handleScroll = (e) => {
    let bottom = e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight
    if (bottom) {
      getTimelineItems(true)
    }
  }

  const onSelectFilter = (filterType) => {
    const foundIndex = activeKeys.indexOf(filterType)
    const updatedKeys = [...activeKeys]

    if (filterType !== "all" && filterType !== "events") {
      const foundIndex = updatedKeys.indexOf("all")
      if (foundIndex !== -1) updatedKeys.splice(foundIndex, 1)
    }

    if (foundIndex === -1) {
      updatedKeys.push(filterType)
    } else {
      updatedKeys.splice(foundIndex, 1)
    }

    if (updatedKeys.length === 0 || (updatedKeys.length === 1 && updatedKeys?.[0] === "events")) {
      updatedKeys.push("all")
    }

    if (updatedKeys.length === 3 && activeKeys.indexOf("all") === -1) {
      const historyIndex = activeKeys.indexOf("history")
      const commentsIndex = activeKeys.indexOf("comments")
      updatedKeys.splice(historyIndex, 1)
      updatedKeys.splice(commentsIndex, 1)
      updatedKeys.push("all")
    }

    setActiveKeys(updatedKeys)
  }

  const contentStyle = {
    backgroundColor: token.colorBgElevated,
    borderRadius: token.borderRadiusLG,
    boxShadow: token.boxShadowSecondary,
    width: 150,
  }

  const panelActions = React.useMemo(() => {
    const actions = []
    if (canUserCreate) {
      actions.push(
        <PrimeButton
          key="add-comment-btn"
          icon={<PlusOutlined />}
          onClick={() => onEditComment()}
        >
          Add comment
        </PrimeButton>
      )
    }
    const menuItems = [
      {
        key: "all",
        label: <a onClick={() => onSelectFilter("all")}>All</a>,
      },
      {
        key: "comments",
        label: <a onClick={() => onSelectFilter("comments")}>Comments</a>,
      },
      {
        key: "events",
        label: (
          <a
            onClick={() => {
              setShowAllComments((val) => !val)
              onSelectFilter("events")
            }}
          >
            {showAllComments ? "Hide" : "Show"} {type === "study" ? "Activity comments/events" : "Study comments"}
          </a>
        ),
      },
      {
        key: "history",
        label: <a onClick={() => onSelectFilter("history")}>History</a>,
      },
    ]
    actions.push(
      <Dropdown
        trigger={"hover"}
        menu={{
          items: menuItems.filter((option) => {
            if (type === "request" && option.key === "events") return false
            return true
          }),
          selectedKeys: activeKeys,
        }}
        selectedActiveKeys={activeKeys}
        dropdownRender={(menu) => {
          return (
            <div
              style={contentStyle}
              className="feed-panel-dropdown-menu"
            >
              {React.cloneElement(menu, {
                style: {
                  boxShadow: "none",
                },
              })}
              <Divider
                style={{
                  margin: 0,
                }}
              />
              <Space
                style={{
                  width: "100%",
                  padding: 8,
                }}
              >
                <PrimeField
                  key="activity-tags"
                  schema={{
                    type: "multiselect",
                    id: "timeline-comments-labels-multiselect",
                    placeholder: "Tags",
                    items: tags || [],
                    onChange: (val) => onChangeLabels(val),
                    maxRows: 1,
                  }}
                  value={Object.keys(selectedLabels).map((key) => key)}
                  containerStyle={{ width: "100%" }}
                  className={"timeline-tags-select"}
                  allowClear
                />
              </Space>
            </div>
          )
        }}
      >
        <PrimeButton icon={<i className="far fa-ellipsis-v" />} />
      </Dropdown>
    )
    return actions
  }, [type, permissions, showAllComments, tags, activeKeys])

  const uniqueDatesRef = React.useRef(null)

  const getDateIsRepeat = (date, index) => {
    let set = uniqueDatesRef?.current || new Set()
    if (index === 0) {
      set = new Set()
    }
    const formatted = dayjs(date).format("MM/DD/YYYY")
    if (set.has(formatted)) {
      return true
    } else {
      set.add(formatted)
      uniqueDatesRef.current = set
      return false
    }
  }

  const renderEmptyFeed = (
    <div className="empty-feed">
      <img
        className="empty_feed_icon"
        src={emptyFeedLogo}
        alt="empty feed container"
      />
      <p className="empty-feed-label">There's no activity, yet</p>
    </div>
  )

  return (
    <Panel
      title="FEED"
      iconClassName="fal fa-newspaper"
      actions={panelActions}
      className={`panel-col feed-panel ${panelClassName}`}
      style={style}
    >
      {timeline?.length > 0 ? (
        <div
          className="workflow-feed"
          onScroll={handleScroll}
        >
          <ul className="workflow-feed-content">
            {timeline
              .filter((timelineItem) => {
                if (activeKeys.includes("all")) {
                  return true
                } else if (activeKeys.includes("history") && timelineItem.item_type === "action") {
                  return true
                } else if (activeKeys.includes("comments") && timelineItem.item_type === "comment") {
                  return true
                }
                return false
              })
              .filter((timelineItem) => {
                if (Object.keys(selectedLabels).length === 0) return true
                if (timelineItem?.tags?.length > 0) {
                  for (let i = 0; i < timelineItem.tags.length; i++) {
                    if (selectedLabels[timelineItem.tags[i]]) return true
                  }
                }
                return false
              })
              .map((timelineItem, index) => {
                const isLastItem = index === timeline.length - 1
                const isDateRepeat = getDateIsRepeat(timelineItem.action_date, index)
                return (
                  <TimelineFeedItem
                    key={timelineItem.id}
                    type={type}
                    data={timelineItem}
                    onEditComment={onEditComment}
                    refetchTimeline={refetchTimeline}
                    isLastItem={isLastItem}
                    isDateRepeat={isDateRepeat}
                  />
                )
              })}
          </ul>
        </div>
      ) : (
        renderEmptyFeed
      )}
      <Modal
        title={selectedComment ? "Edit Comment" : "New Comment"}
        message={modalMsg}
        open={openEditCommentModal}
        onCancel={onCloseEditComment}
        hideImage={true}
        width={520}
        footer={[]}
      >
        <FeedCommentEditForm
          type={type}
          timelineFeedParams={timelineFeedParams}
          selectedComment={selectedComment}
          onCloseEditComment={onCloseEditComment}
          refetchTimeline={refetchTimeline}
        />
      </Modal>
    </Panel>
  )
}

TimelineFeed.propTypes = {
  type: PropTypes.oneOf(["study", "activity", "request"]),
  timeline: PropTypes.arrayOf(
    PropTypes.shape({
      tags: PropTypes.arrayOf(PropTypes.string),
      id: PropTypes.number,
      action_date: PropTypes.string,
    })
  ),
}

export default TimelineFeed
